import React, { useEffect, useState, useMemo } from 'react';
import {
  getRevenueTrend,
  getRevenueByCategory,
  getRevenueByPaymentMethod
} from '../../services/analytics.service';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  PieChart,
  Pie,
  Cell
} from 'recharts';


const COLORS = ['#3b82f6', '#10b981', '#ef4444', '#f59e0b', '#6366f1', '#8b5cf6'];

/* ------------------------------------
   Spinner Overlay (Reusable)
------------------------------------ */
const ChartLoader = () => (
  <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10">
    <div className="h-10 w-10 rounded-full border-4 border-gray-200 border-t-blue-500 animate-spin" />
  </div>
);
const ChartEmptyState = ({ message }) => (
  <div className="absolute inset-0 flex items-center justify-center text-gray-500 z-10">
    <p className="text-sm text-center">{message}</p>
  </div>
);



/* ------------------------------------
   Revenue Insights
------------------------------------ */
const RevenueInsights = ({ filters }) => {
  const [trendData, setTrendData] = useState([]);
  const [byCategoryData, setByCategoryData] = useState([]);
  const [paymentData, setPaymentData] = useState([]);
  const [loading, setLoading] = useState(false);
  

  const formatRevenueDateLabel = (dateStr, bucket = 'day') => {
  if (!dateStr) return '';

  const d = new Date(dateStr);
  if (isNaN(d)) return '';

  // Day bucket → "1 Jan"
  if (bucket === 'day') {
    return d.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short'
    });
  }

  // Month / other → "Jan 2026"
  return d.toLocaleDateString(undefined, {
    month: 'short',
    year: 'numeric'
  });
};


  /* ------------------------------------
     Fetch Data (Parallel)
  ------------------------------------ */
 useEffect(() => {
  const controller = new AbortController();

  const params =
    filters?.from && filters?.to
      ? { from: filters.from, to: filters.to }
      : { range: filters?.range || 'today' };

  const fetchData = async () => {
    try {
      setLoading(true);

      const [
        trendRes,
        categoryRes,
        paymentRes
      ] = await Promise.all([
        getRevenueTrend(params, controller.signal),
        getRevenueByCategory(params, controller.signal),
        getRevenueByPaymentMethod(params, controller.signal)
      ]);

      if (controller.signal.aborted) return;

      setTrendData(trendRes?.data || []);
      setByCategoryData(categoryRes || []);
      setPaymentData(paymentRes || []);
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('RevenueInsights request aborted');
        return;
      }

      console.error('RevenueInsights load failed', err);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  };

  fetchData();

  return () => {
    controller.abort();
  };
}, [filters?.from, filters?.to, filters?.range]);

  /* ------------------------------------
     Memoized Data
  ------------------------------------ */
  const safeTrendData = useMemo(
    () => trendData.filter(r => r?.DateLabel),
    [trendData]
  );

  const safePaymentData = useMemo(
    () => paymentData.filter(p => p?.Revenue > 0),
    [paymentData]
  );
  const hasTrendData = useMemo(() => {
  return safeTrendData.some(d => (d.Revenue || 0) > 0);
}, [safeTrendData]);

const hasCategoryData = useMemo(() => {
  return byCategoryData.some(d => (d.TotalAmount || 0) > 0);
}, [byCategoryData]);

const hasPaymentData = useMemo(() => {
  return safePaymentData.some(d => (d.Revenue || 0) > 0);
}, [safePaymentData]);

const shouldHideXAxisTicks = useMemo(() => {
  if (!filters?.from || !filters?.to) return false;

  const from = new Date(filters.from);
  const to = new Date(filters.to);

  const diffDays = Math.ceil((to - from) / (1000 * 60 * 60 * 24));

  return diffDays > 15;
}, [filters?.from, filters?.to]);


  return (
    <div className="revenue-insights space-y-10">

      {/* ==============================
         Revenue Trend
      ============================== */}
      <div className="card bg-white shadow-lg rounded-xl p-6 relative">
        <h2 className="text-xl font-semibold mb-4">📈 Revenue Trend</h2>

        <div className="relative" style={{ height: 320 }}>
          {loading && <ChartLoader />}

          {!loading && !hasTrendData && (
            <ChartEmptyState message="No revenue data available for this period" />
          )}


          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={safeTrendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
  dataKey="DateLabel"
  tickFormatter={(value) =>
    shouldHideXAxisTicks ? '' : formatRevenueDateLabel(value, 'day')
  }
  axisLine={true}
  tickLine={!shouldHideXAxisTicks}
/>
              <YAxis
                width={60}
                tickFormatter={(v) =>
                  v >= 1_000_000 ? `${v / 1_000_000}M`
                  : v >= 1_000 ? `${v / 1_000}k`
                  : v
                }
              />
              <Tooltip formatter={(v) => v.toLocaleString()} />
              <Legend />
              <Line
                dataKey="Revenue"
                stroke="#3b82f6"
                strokeWidth={3}
                dot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ==============================
         Revenue by Category
      ============================== */}
      <div className="card bg-white shadow-lg rounded-xl p-6 relative">
        <h2 className="text-xl font-semibold mb-4">📊 Revenue by Category</h2>

        <div className="relative" style={{ height: 300 }}>
           {loading && <ChartLoader />}

          {!loading && !hasCategoryData && (
            <ChartEmptyState message="No category-wise revenue data found" />
          )}

          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={byCategoryData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="CategoryName" hide />
              <YAxis
                width={60}
                tickFormatter={(v) =>
                  v >= 1_000_000 ? `${v / 1_000_000}M`
                  : v >= 1_000 ? `${v / 1_000}k`
                  : v
                }
              />
              <Tooltip formatter={(v) => v.toLocaleString()} />
              <Legend />
              <Bar dataKey="TotalAmount" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ==============================
         Payment Method Split
      ============================== */}
      <div className="card bg-white shadow-lg rounded-xl p-6 relative">
        <h2 className="text-xl font-semibold mb-4">🧾 Payment Method Split</h2>

        <div className="relative" style={{ height: 300 }}>
           {loading && <ChartLoader />}
            {!loading && !hasPaymentData && (
              <ChartEmptyState message="No payment method data available" />
            )}


          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={safePaymentData}
                dataKey="Revenue"
                nameKey="PaymentMethod"
                innerRadius={60}
                outerRadius={100}
              >
                {safePaymentData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => v.toLocaleString()} />
              <Legend
              verticalAlign="bottom"
              height={60}
              formatter={(value) => {
                const revenue = paymentData.find((item) => item.PaymentMethod === value)?.Revenue || 0;
                return `${value}: ${revenue.toLocaleString()}`;
              }}
            />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

    </div>
  );
};

export default RevenueInsights;
